import { Link, useLocation } from 'react-router-dom';
import { cn } from '@/lib/utils';
import {
  LayoutDashboard,
  User,
  FileText,
  Settings,
  ArrowLeftRight,
  MessageSquare,
  MapPin,
  Map,
  ChevronLeft,
  ChevronRight,
  Target,
  CircleHelp,
  ShieldCheck,
} from 'lucide-react';
import { Button } from '@/components/ui/button';

interface SidebarProps {
  isOpen: boolean;
  onToggle: () => void;
}

type MenuItem = {
  icon: typeof LayoutDashboard;
  label: string;
  href: string;
  iconClass?: string;
};

type MenuSection = {
  section: string;
  items: MenuItem[];
};

const menuSections: MenuSection[] = [
  {
    section: 'Menu Utama',
    items: [
      { icon: LayoutDashboard, label: 'Beranda', href: '/' },
      { icon: User, label: 'Pengguna', href: '/users' },
      { icon: Target, label: 'Program', href: '/program' },
      { icon: ArrowLeftRight, label: 'Transaksi', href: '/donasi', iconClass: 'rotate-45' },
      { icon: FileText, label: 'Berita', href: '/news' },
      { icon: CircleHelp, label: 'FAQ', href: '/faq' },
      { icon: MessageSquare, label: 'Umpan Balik', href: '/feedback' },
      { icon: ShieldCheck, label: 'Kebijakan Privasi', href: '/privacy-policy' },
      { icon: ShieldCheck, label: 'Ketentuan Layanan', href: '/term-and-conditions' },
    ],
  },
  {
    section: 'Master Data',
    items: [
      { icon: MapPin, label: 'Kota', href: '/kota' },
      { icon: Map, label: 'Provinsi', href: '/provinsi' },
      { icon: FileText, label: 'Kategori Berita', href: '/news-categories' },
    ],
  },
  {
    section: 'Pengaturan',
    items: [{ icon: Settings, label: 'Pengaturan', href: '/pengaturan' }],
  },
];

export function Sidebar({ isOpen, onToggle }: SidebarProps) {
  const location = useLocation();

  const isItemActive = (href: string) => {
    if (href === '/') return location.pathname === '/';
    if (href === '/provinsi') return location.pathname === '/provinsi' || location.pathname === '/propinsi';
    return location.pathname === href || location.pathname.startsWith(`${href}/`);
  };

  return (
    <aside
      className={cn(
        'fixed left-0 top-0 z-40 h-screen bg-gray-900 transition-all duration-300',
        isOpen ? 'w-64' : 'w-20'
      )}
    >
      <div className="flex h-full flex-col">
        <div className="flex h-16 items-center justify-between border-b border-gray-800 px-4">
          <Link to="/" className="flex items-center gap-2">
            <img src="/logo.png" alt="SMALA" className="h-8 w-8 rounded object-contain" />
            {isOpen && <span className="text-lg font-semibold text-white">SMALA</span>}
          </Link>
          <Button
            variant="ghost"
            size="icon"
            onClick={onToggle}
            className="text-gray-400 hover:text-white"
          >
            {isOpen ? <ChevronLeft className="h-5 w-5" /> : <ChevronRight className="h-5 w-5" />}
          </Button>
        </div>

        <nav className="scrollbar-none flex-1 overflow-y-auto py-4">
          <div className="space-y-5 px-2">
            {menuSections.map((menuSection) => (
              <div key={menuSection.section} className="space-y-2">
                {isOpen && (
                  <p className="px-3 text-[11px] font-semibold uppercase tracking-wider text-gray-500">
                    {menuSection.section}
                  </p>
                )}
                <ul className="space-y-1">
                  {menuSection.items.map((item) => {
                    const isActive = isItemActive(item.href);
                    const Icon = item.icon;

                    return (
                      <li key={item.href}>
                        <Link
                          to={item.href}
                          className={cn(
                            'flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
                            isActive
                              ? 'bg-primary text-primary-foreground'
                              : 'text-gray-400 hover:bg-gray-800 hover:text-white',
                            !isOpen && 'justify-center'
                          )}
                          title={!isOpen ? item.label : undefined}
                        >
                          <Icon className={cn('h-5 w-5 shrink-0', item.iconClass)} />
                          {isOpen && <span>{item.label}</span>}
                        </Link>
                      </li>
                    );
                  })}
                </ul>
              </div>
            ))}
          </div>
        </nav>

        <div className="border-t border-gray-800 p-4">
          <p className={cn('text-xs text-gray-500', !isOpen && 'text-center')}>
            {isOpen ? '© 2024 SMALA. Hak cipta dilindungi.' : '© 2024'}
          </p>
        </div>
      </div>
    </aside>
  );
}
